/**
 * Hydrates deferred videos and supports optional autoplay behavior.
 *
 * @return {void}
 */
const initMediaVideo = () => {

	// Skip videos already bound by another copy of this runtime - double
	// binding makes the click toggle pause and resume in the same click.
	const videos = Array.from( document.querySelectorAll( '.js-wombat-media-video[data-src]' ) )
		.filter( ( video ) => ! video.hasAttribute( 'data-wombat-video-ready' ) );

	if ( videos.length === 0 ) {

		return;

	}

	videos.forEach( ( video ) => video.setAttribute( 'data-wombat-video-ready', '' ) );

	/**
	 * Resolve autoplay mode from element data attribute.
	 *
	 * @param {HTMLElement} video Video element.
	 * @return {boolean}
	 */
	const isAutoplayEnabled = ( video ) => video.getAttribute( 'data-autoplay' ) !== 'false';

	/**
	 * Get video wrapper used to toggle "started" state.
	 *
	 * @param {HTMLElement} video Video element.
	 * @return {HTMLElement|null}
	 */
	const getVideoWrapper = ( video ) => video.closest( '.wombat-media-video' );

	/**
	 * Load deferred source once.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @return {void}
	 */
	const hydrateVideoSource = ( video ) => {

		const source = video.getAttribute( 'data-src' );

		if ( ! source || video.getAttribute( 'src' ) ) {

			return;

		}

		video.setAttribute( 'src', source );
		video.setAttribute( 'preload', 'auto' );
		video.removeAttribute( 'data-src' );
		video.load();

	};

	/**
	 * Render poster overlay for legacy markup saved with `data-poster`
	 * (blocks not re-saved since the poster moved to a separate <img>).
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @return {void}
	 */
	const renderLegacyPoster = ( video ) => {

		const poster = video.getAttribute( 'data-poster' );
		const wrapper = getVideoWrapper( video );

		video.removeAttribute( 'data-poster' );

		if ( ! poster || ! wrapper || wrapper.querySelector( '.wombat-media-video__poster' ) ) {

			return;

		}

		const image = document.createElement( 'img' );
		image.className = 'wombat-media-video__poster';
		image.src = poster;
		image.alt = '';
		image.loading = 'lazy';
		image.decoding = 'async';

		video.after( image );

	};

	/**
	 * Release the forced 16:9 box once real video dimensions are known,
	 * avoiding a flash where the container shrinks before resizing.
	 *
	 * @param {HTMLVideoElement} video   Video element.
	 * @param {HTMLElement}      wrapper Video wrapper.
	 * @return {void}
	 */
	const unlockVideoAspectRatio = ( video, wrapper ) => {

		if ( ! wrapper ) {

			return;

		}

		if ( video.readyState >= 1 ) {

			wrapper.classList.add( 'is-video-sized' );
			return;

		}

		video.addEventListener( 'loadedmetadata', () => {

			wrapper.classList.add( 'is-video-sized' );

		}, { once: true } );

	};

	/**
	 * Mark video as started and attempt playback.
	 *
	 * @param {HTMLVideoElement} video             Video element.
	 * @param {boolean}          addAutoplayAttr  Should set autoplay attribute.
	 * @return {void}
	 */
	const startVideoPlayback = ( video, addAutoplayAttr = false ) => {

		const wrapper = getVideoWrapper( video );

		hydrateVideoSource( video );
		if ( addAutoplayAttr ) {

			video.setAttribute( 'autoplay', '' );

		}

		const playPromise = video.play();
		if ( playPromise && typeof playPromise.catch === 'function' ) {

			playPromise.catch( () => undefined );

		}

		if ( wrapper ) {

			wrapper.classList.add( 'is-video-started' );

		}

		unlockVideoAspectRatio( video, wrapper );

	};

	/**
	 * Pause autoplay video once it leaves the viewport.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @return {void}
	 */
	const pauseVideo = ( video ) => {

		video.pause();

	};

	/**
	 * Attach click behavior to custom play button.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @return {void}
	 */
	const bindManualPlay = ( video ) => {

		const wrapper = getVideoWrapper( video );
		const playButton = wrapper?.querySelector( '.js-wombat-media-video-play' );

		if ( ! playButton ) {

			return;

		}

		playButton.addEventListener( 'click', ( event ) => {

			event.preventDefault();
			startVideoPlayback( video, false );

		} );

		video.addEventListener( 'play', () => {

			wrapper?.classList.add( 'is-video-started' );

		} );

	};

	/**
	 * Add minimal click toggle for videos without controls.
	 * Enabled only when autoplay is disabled and controls are hidden.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @return {void}
	 */
	const bindManualToggleWithoutControls = ( video ) => {

		const shouldEnableToggle = ! isAutoplayEnabled( video ) && ! video.hasAttribute( 'controls' );
		if ( ! shouldEnableToggle ) {

			return;

		}

		video.addEventListener( 'click', ( event ) => {

			event.preventDefault();

			if ( ! video.getAttribute( 'src' ) && video.getAttribute( 'data-src' ) ) {

				startVideoPlayback( video, false );
				return;

			}

			if ( video.paused ) {

				startVideoPlayback( video, false );
				return;

			}

			video.pause();

		} );

	};

	const autoplayVideos = videos.filter( ( video ) => isAutoplayEnabled( video ) );
	const manualVideos = videos.filter( ( video ) => ! isAutoplayEnabled( video ) );

	manualVideos.forEach( ( video ) => {

		renderLegacyPoster( video );
		bindManualPlay( video );
		bindManualToggleWithoutControls( video );

	} );

	if ( autoplayVideos.length === 0 ) {

		if ( 'IntersectionObserver' in window ) {

			const manualObserver = new IntersectionObserver(
				( entries ) => {

					entries.forEach( ( entry ) => {

						if ( entry.isIntersecting ) {

							return;

						}

						pauseVideo( entry.target );

					} );

				},
				{
					root: null,
					rootMargin: '150px 0px',
					threshold: 0,
				}
			);

			manualVideos.forEach( ( video ) => {

				manualObserver.observe( video );

			} );

		}

		return;

	}

	if ( 'IntersectionObserver' in window ) {

		const observer = new IntersectionObserver(
			( entries ) => {

				entries.forEach( ( entry ) => {

					const video = entry.target;
					if ( entry.isIntersecting ) {

						if ( isAutoplayEnabled( video ) ) {

							startVideoPlayback( video, true );

						}
						return;

					}

					pauseVideo( video );

				} );

			},
			{
				root: null,
				rootMargin: '150px 0px',
				threshold: 0,
			}
		);

		videos.forEach( ( video ) => {

			observer.observe( video );

		} );

		return;

	}

	autoplayVideos.forEach( ( video ) => {

		startVideoPlayback( video, true );

	} );

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', initMediaVideo );

} else {

	initMediaVideo();

}
